import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { ProjectName } from "../../constants/main";
import { MyProfilePath, ProjectRelativePath } from "../../routes/paths";
import { observer } from "mobx-react-lite";
import ChangeLanguageBtn from "../../i18n/ChangeLanguageBtn";
import i18nStore from "../../i18n/store";
import filterStore from "../CalculationsPage/components/filters/filtersStore";
import CurrentProjectChip from "../project/components/CurrentProjectChip";
import ProjectMenuDrawer from "./ProjectMenuDrawer";
import authUserStore from "../auth/store";
import { AccountCircle } from "@mui/icons-material";
import { Menu } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useRef } from "react";
import useComponentSize from "../../hooks/useComponentSize";
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';


const drawerWidth = 240;


export const DrawerHeader = styled('div')(({theme}) => ({
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));


interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}


const CustomAppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme}) => ({
    zIndex: theme.zIndex.drawer + 1,
    variants: [
        {
            props: ({open}) => open,
            style: {
                marginLeft: drawerWidth,
            },
        },
    ],
}));


const MiniDrawer = observer(() => {
    const translations = i18nStore.getTranslations;
    const navigate = useNavigate();
    const params = useParams();
    const isInProject: boolean = Boolean(params.projectId);

    const ProjectMenuDrawerRef: React.MutableRefObject<any> = useRef();
    const gridWrapperSize = useComponentSize(ProjectMenuDrawerRef);

    const [open, setOpen] = React.useState(false);

    const toggleDrawerOpen = () => {
        setOpen(!open);
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <CustomAppBar position="fixed" open={open}>
                <Toolbar variant="dense" sx={{ px: {xs: 2, md: 2,} }}>
                    {
                        isInProject &&
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawerOpen}
                            edge="start"
                            sx={[
                                {
                                    marginRight: {xs: 0, md: 2},
                                },
                            ]}
                        >
                            {open && <ChevronLeftIcon/>}
                            {!open && <MenuIcon/>}
                        </IconButton>
                    }

                    <a href="/" style={{ textDecoration: 'none' }}>
                        <Button
                            sx={{
                                color: "white",
                                fontSize: "1rem",
                                "&:hover": {color: "#bad3ff"},
                                display: {xs: "none", md: "block"},
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigate("/");
                            }}
                        >
                            {ProjectName}
                        </Button>
                    </a>
                    <CurrentProjectChip />
                    <Box sx={{
                        display: "flex",
                        ml: "auto",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                        {
                            authUserStore.isLogin &&
                            <a href={`/${ProjectRelativePath}`}>
                                <Button
                                    sx={{ mx: {xs: 0, md: 1}, color: "white", "&:hover": {color: "#bad3ff"} }}
                                    startIcon={<SnippetFolderIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        navigate(`/${ProjectRelativePath}`);
                                    }}
                                >
                                    {translations.projects}
                                </Button>
                            </a>
                        }
                        {/*<a href={DocsUrl}>*/}
                        {/*    <Button*/}
                        {/*        sx={{mx: 1, color: "white", "&:hover": {color: "#bad3ff"}}}*/}
                        {/*        startIcon={<AutoStoriesIcon/>}*/}
                        {/*        onClick={() => {*/}
                        {/*            navigate(DocsUrl);*/}
                        {/*        }}*/}
                        {/*    >*/}
                        {/*        {translations.docs}*/}
                        {/*    </Button>*/}
                        {/*</a>*/}

                        <ChangeLanguageBtn/>
                        {authUserStore.isLogin && (
                            <div>
                                <IconButton
                                    size="large"
                                    aria-label="account of current user"
                                    aria-controls="menu-appbar"
                                    aria-haspopup="true"
                                    onClick={handleMenu}
                                    color="inherit"
                                >
                                    <AccountCircle />
                                </IconButton>
                                <MainUserDropdownMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
                            </div>
                        )}
                    </Box>
                </Toolbar>
            </CustomAppBar>
            <ProjectMenuDrawer open={open} ProjectMenuDrawerRef={ProjectMenuDrawerRef} />
            <Box component="main" sx={{flexGrow: 0, px: 1, width: `calc(100% - ${gridWrapperSize.width}px)`}}>
                <DrawerHeader/>
                <Outlet/>
            </Box>
        </Box>
    );
});

export default MiniDrawer;


interface MainUserDropdownMenuProps {
    anchorEl: any;
    setAnchorEl: (anchorEl: any) => void;
}
function MainUserDropdownMenu(props: MainUserDropdownMenuProps) {
    const translations = i18nStore.getTranslations;
    const navigate = useNavigate();

    const handleClose = () => {
        props.setAnchorEl(null);
    };

    return (
        <>
        <Menu
            id="menu-appbar"
            anchorEl={props.anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            // keepMounted
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            sx={{top: 40}}
            open={Boolean(props.anchorEl)}
            onClose={handleClose}
        >
            <Box sx={{px: 2, pb: 1,}}>
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    component="span"
                >
                    {authUserStore.getMe?.name || ""}
                </Typography>

                <Typography variant="body1" fontSize={"0.9rem"} color="textDisabled">
                    {authUserStore.getMe?.email || ""}
                </Typography>
            </Box>
            <Divider sx={{mb: 1}}/>
            <a href={MyProfilePath} style={{ textDecoration: 'none', color: 'inherit', }}>
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigate(MyProfilePath);
                    handleClose();
                }}>
                    <ListItemIcon>
                        <ManageAccountsIcon/>
                    </ListItemIcon>
                    <ListItemText>
                        {translations._profile.profileLink}
                    </ListItemText>

                </MenuItem>
            </a>
        <Divider sx={{mb: 1}}/>
        <MenuItem onClick={() => {
                    authUserStore.delete();
                    filterStore.delete();
                    handleClose();
                }}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText>
                        {translations.logout}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}